import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import config from '@/config';
import SKClubAuth from '@/components/sk-club/SKClubAuth';
import SKClubDashboard from '@/components/sk-club/SKClubDashboard';
import SKClubExchange from '@/components/sk-club/SKClubExchange';
import useSKClubAuth from '@/hooks/useSKClubAuth';

const FloatingBubble = ({ className, delay = 0, duration = 4 }) => (
  <motion.div
    className={clsx('absolute rounded-full opacity-20', className)}
    animate={{
      y: [0, -20, 0],
      scale: [1, 1.1, 1],
    }}
    transition={{
      duration,
      delay,
      repeat: Infinity,
      ease: 'easeInOut',
    }}
  />
);

const FloatingFish = ({ className, delay = 0 }) => (
  <motion.div
    className={clsx('absolute text-4xl md:text-6xl opacity-10 select-none', className)}
    animate={{
      x: [0, 30, 0],
      y: [0, -10, 0],
      rotate: [0, 5, -5, 0],
    }}
    transition={{
      duration: 6,
      delay,
      repeat: Infinity,
      ease: 'easeInOut',
    }}
  >
    🐟
  </motion.div>
);

const GameSection = ({ title, logo, tagline, description, video, status, appStoreUrl, playStoreUrl, detailHref }) => {
  const hasStoreLinks = appStoreUrl || playStoreUrl;
  const isComingSoon = status?.toLowerCase().includes('coming soon');

  const VideoWrap = ({ children }) =>
    detailHref ? (
      <Link href={detailHref}>
        <a className='block group/video focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded-3xl'>{children}</a>
      </Link>
    ) : (
      <>{children}</>
    );

  return (
    <section className='relative overflow-hidden'>
      <FloatingBubble className='w-32 h-32 bg-primary-500 top-10 left-10' delay={0} />
      <FloatingBubble className='w-20 h-20 bg-secondary-500 top-1/4 right-20' delay={1} />
      <FloatingBubble className='w-16 h-16 bg-blue-400 bottom-1/4 left-32' delay={2} />
      <FloatingFish className='right-16 top-20' delay={0} />
      <FloatingFish className='left-20 bottom-20' delay={2} />

      <div className='relative z-10 py-16 md:py-24'>
        <div className='px-4 mx-auto lg:px-8 max-w-7xl'>
          <div className='grid lg:grid-cols-2 gap-8 lg:gap-16 items-center'>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className='relative'
            >
              <VideoWrap>
                <div className='absolute -inset-4 bg-gradient-to-r from-primary-500/20 via-secondary-500/20 to-blue-500/20 rounded-3xl blur-xl group-hover/video:opacity-80 transition-opacity' />
                <div className='relative rounded-2xl overflow-hidden shadow-2xl border-4 border-gray-700/50 group-hover/video:border-primary-500/60 transition-colors'>
                  <video autoPlay loop muted playsInline className='w-full aspect-[4/3] object-cover'>
                    <source src={video} type='video/mp4' />
                  </video>
                  <div className='absolute inset-0 bg-gradient-to-t from-gray-900/60 via-transparent to-transparent' />
                </div>
              </VideoWrap>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className='text-center lg:text-left'
            >
              <motion.span
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.3 }}
                className='inline-block px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white text-sm font-bold rounded-full mb-6 shadow-lg shadow-primary-500/25'
              >
                {status}
              </motion.span>

              {logo ? (
                detailHref ? (
                  <Link href={detailHref}>
                    <a className='inline-block mb-4 max-w-[220px] mx-auto lg:mx-0 hover:scale-105 transition-transform'>
                      <Image src={logo} alt={title} width={500} height={339} className='w-full h-auto' />
                    </a>
                  </Link>
                ) : (
                  <div className='mb-4 max-w-[220px] mx-auto lg:mx-0'>
                    <Image src={logo} alt={title} width={500} height={339} className='w-full h-auto' />
                  </div>
                )
              ) : (
                <h2 className='text-3xl font-extrabold text-neutral-200 md:text-5xl mb-4'>{title}</h2>
              )}

              <p className='text-xl md:text-2xl text-primary-400 font-medium mb-4'>{tagline}</p>

              <p className='text-neutral-400 text-base md:text-lg mb-8 max-w-lg mx-auto lg:mx-0'>{description}</p>

              {(hasStoreLinks || detailHref) && (
                <div className='flex flex-col items-center lg:items-start gap-5'>
                  {hasStoreLinks && (
                    <div className='flex flex-wrap items-center justify-center lg:justify-start gap-4'>
                      {appStoreUrl && (
                        <motion.a
                          href={isComingSoon ? undefined : appStoreUrl}
                          target={isComingSoon ? undefined : '_blank'}
                          rel={isComingSoon ? undefined : 'noopener noreferrer'}
                          whileHover={isComingSoon ? {} : { scale: 1.05 }}
                          whileTap={isComingSoon ? {} : { scale: 0.95 }}
                          className={clsx('transition-opacity inline-block', isComingSoon ? 'opacity-40 cursor-not-allowed' : 'hover:opacity-80')}
                        >
                          <img src='/badges/app-store-badge.svg' alt='Download on the App Store' className='h-[60px] w-auto' />
                        </motion.a>
                      )}
                      {playStoreUrl && (
                        <motion.a
                          href={isComingSoon ? undefined : playStoreUrl}
                          target={isComingSoon ? undefined : '_blank'}
                          rel={isComingSoon ? undefined : 'noopener noreferrer'}
                          whileHover={isComingSoon ? {} : { scale: 1.05 }}
                          whileTap={isComingSoon ? {} : { scale: 0.95 }}
                          className={clsx('transition-opacity inline-block', isComingSoon ? 'opacity-40 cursor-not-allowed' : 'hover:opacity-80')}
                        >
                          <img src='/badges/google-play-badge.svg' alt='Get it on Google Play' className='h-[60px] w-auto' />
                        </motion.a>
                      )}
                    </div>
                  )}
                  {detailHref && (
                    <Link href={detailHref}>
                      <a className='inline-flex items-center gap-2 text-primary-400 hover:text-primary-300 font-bold text-base transition-colors group'>
                        Learn more about the game
                        <span className='transition-transform group-hover:translate-x-1'>→</span>
                      </a>
                    </Link>
                  )}
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

const frikdUpFishiesData = {
  title: "Frik'd Up Fishies",
  logo: '/sk-club/logo-frikd.png',
  tagline: 'Kitties taking care of fish. What could go wrong?',
  description:
    "Build your aquarium. Collect fish. Let your kitties handle the rest. They promise they're not hungry. Available now on iOS and Android.",
  video: '/sk-club/frikd-up-fishies-v2.mov',
  status: 'Available Now',
  appStoreUrl: 'https://apps.apple.com/us/app/sk-club-frikd-up-fishies/id6755168106',
  playStoreUrl: 'https://play.google.com/store/apps/details?id=com.minnerlabs.FrikdUpFishies',
  detailHref: '/sk-club/frikd-up-fishies',
};

const SKClubIntegration = () => {
  const { isAuthenticated, loading } = useSKClubAuth();

  if (loading) {
    return (
      <div className='flex items-center justify-center py-16'>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
          className='w-8 h-8 border-2 border-gray-700 border-t-primary-500 rounded-full'
        />
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <SKClubAuth />
      {isAuthenticated && (
        <>
          <SKClubDashboard />
          {config.skClub.exchangeEnabled && <SKClubExchange />}
        </>
      )}
    </div>
  );
};

export default function SKClubPageDetail() {
  return (
    <>
      <div className='bg-gray-900 overflow-hidden relative'>
        <div className='absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary-500/10 via-transparent to-transparent' />

        <div className={clsx('px-4 mx-auto lg:px-8 max-w-7xl relative')}>
          <div className='pt-24 pb-12 md:pt-32 md:pb-16 relative flex flex-col items-center text-center'>
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className='mb-8 max-w-[250px] mx-auto'
            >
              <Image src='/sk-club/sk-club-horizontal.png' alt='SK Club Logo' width={800} height={200} className='w-full' />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className='text-3xl font-bold text-neutral-200 md:text-5xl max-w-2xl'
            >
              Games & Mobile Apps by <span className='text-primary-500'>Shitty Kitties</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className='text-base text-neutral-400 md:text-xl mt-4 max-w-2xl'
            >
              SK Club creates mobile games and apps that tie into the Shitty Kitties ecosystem. Some chill & cozy, some chaotic, all uniquely Shitty.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className='mt-4 max-w-[200px] md:max-w-[300px]'
            >
              <Image src='/accents/underline.png' alt='accent underline' width={722} height={121} />
            </motion.div>
          </div>
        </div>
      </div>

      <div className='bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 overflow-hidden'>
        <GameSection {...frikdUpFishiesData} />
      </div>

      {config.skClub.enabled && (
        <div className='bg-gray-900 relative overflow-hidden'>
          <div className='absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_var(--tw-gradient-stops))] from-primary-500/5 via-transparent to-transparent' />
          <FloatingBubble className='w-24 h-24 bg-primary-500 top-20 right-16' delay={1} duration={5} />
          <FloatingBubble className='w-16 h-16 bg-secondary-500 bottom-32 left-12' delay={2} duration={4} />
          <FloatingFish className='left-8 top-32' delay={3} />

          <div className='relative z-10 py-16 md:py-24'>
            <div className='px-4 mx-auto lg:px-8 max-w-3xl'>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className='text-center mb-10'
              >
                <h2 className='text-2xl font-extrabold text-neutral-300 md:text-4xl'>
                  SK Club Account
                </h2>
                <div className='max-w-[140px] md:max-w-[220px] mt-4 mx-auto'>
                  <Image src='/accents/underline.png' alt='' width={722} height={121} />
                </div>
                <p className='text-neutral-400 text-base md:text-lg mt-4 max-w-md mx-auto'>
                  Link your wallet and exchange TREATS for&nbsp;in-game&nbsp;coins
                </p>
              </motion.div>
              <SKClubIntegration />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
